import React from "react";
import { Printer, MessageCircle, ShieldCheck, XCircle, CheckCircle2, Megaphone, Link2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { CLINIC, LogoTile, doctorDisplay, doctorPhotoUrl, brandColor } from "../lib/clinic";
import { openWhatsAppRaw } from "../lib/whatsapp";

const SECTIONS = [
  {
    title: "1 · The Prescription Itself",
    pains: [
      "NMC mandates legible prescriptions — every scribbled Rx is a latent lawsuit.",
      "70% of prescriptions repeat the same 15–20 combinations, rewritten 50 times a day.",
      "Dose slips at patient #47 are a human inevitability, not a character flaw.",
      "\u201CDoctor, yeh kitne ka padega?\u201D — and you genuinely don't know.",
    ],
    solutions: [
      "Print-perfect bilingual Rx (EN/मराठी/हिंदी) with letterhead, Reg No, signature and a tamper-evident Verify-Rx QR.",
      "27,000+ drug price list → live estimated ₹ cost of the whole Rx + one-tap \u201CCheapest Rx\u201D brand swap.",
      "\u201CMy Choice\u201D learns your preferred brands and stock, with exact pack-size price math.",
      "Templates + \u201CCt all\u201D recall the entire last Rx in one tap; dose-ceiling checks stop the slip at the tap.",
    ],
  },
  {
    title: "2 · Patient Education — the 15 minutes you never have",
    pains: [
      "Insulin technique, hypo rules, foot care take 10–15 min per patient — impossible at OPD scale.",
      "Patients forget 40–80% of verbal instructions before the parking lot; non-adherence circles back as \u201Cilaj chal nahi raha\u201D.",
      "Handouts, if any, are English-only photocopies of photocopies.",
    ],
    solutions: [
      "Auto-detected bilingual Education Kits — GLP-1, Insulin, DKD, Heart Failure, Inhaler, Thyroid, Blood-thinner Safety, New Hypertension — surface the moment you prescribe or tag the condition.",
      "Every kit carries your logo, photo, Reg No and contacts — when it's forwarded on WhatsApp, your clinic's name travels with it.",
      "One-tap WhatsApp Kit sends the full kit + secure 30-day link to the patient's own chat.",
      "AI Diet Chart Builder and exercise plans, printable and WhatsApp-able in three languages.",
    ],
  },
  {
    title: "3 · Medico-legal Compliance — the 2 a.m. worry",
    pains: [
      "CPA 2019 covers medical services; crore-level claims are no longer rare headlines.",
      "In court the question is \u201Ccan you PROVE you considered it?\u201D — paper proves nothing.",
      "Pregnancy, renal failure, documented allergy — the classic negligence fact-patterns happen in the 8 busy seconds of OPD.",
    ],
    solutions: [
      "Tap-time interception: eGFR, pregnancy/lactation, G6PD, allergy and contraindication checks BEFORE the drug lands on the Rx.",
      "Every warning and every conscious override is permanently audit-logged with user + timestamp — a court-ready trail.",
      "Full Audit page, role-based access (reception locked out of clinical screens), QR-verifiable prescriptions.",
      "SJS/TEN cross-reactivity flags and a 718-drug pregnancy reference with cited monographs one tap away.",
    ],
  },
  {
    title: "4 · Adult Vaccination — the forever-forgotten opportunity",
    pains: [
      "Every diabetic ≥50 deserves flu/pneumococcal/HepB/Tdap/zoster consideration — real-world coverage is single-digit, purely from forgetting to remember.",
      "An ethical revenue line quietly leaking away.",
    ],
    solutions: [
      "Vaccines Planner inside the visit flow — age- and comorbidity-appropriate suggestions with schedules.",
      "Dated vaccine recalls with reminders; advice prints on the Rx (the medico-legally correct posture).",
    ],
  },
  {
    title: "5 · Follow-ups — where chronic care quietly dies",
    pains: [
      "The patient who skips the 3-month review returns in 3 years with retinopathy — and a grievance.",
      "No way to know who lapsed, who never collected reports, who never even opened what you sent.",
    ],
    solutions: [
      "Reminders engine + Refills queue on the dashboard with one-tap WhatsApp nudges.",
      "Kit Scan Tracker — every patient marked Read / Opened / Not opened, with a branded reminder for the silent ones. Engagement telemetry even hospital EMRs don't give.",
      "The printed Rx carries a \u201CYour Care Kit\u201D QR reopening the full Rx + kits at home for 30 days (OTP-protected).",
    ],
  },
  {
    title: "6 · Drug–Drug Interactions — the polypharmacy minefield",
    pains: [
      "6–9 molecules per T2DM patient + the cardiologist's, the orthopaedician's, and \u201Cbas ayurvedic\u201D — beyond any working memory.",
      "Errors happen precisely because each prescriber was individually reasonable.",
    ],
    solutions: [
      "Curated DDI engine fires AT THE TAP — severity-graded, with the clinical reason stated, across the whole Rx in context.",
      "Condition-based contraindications (conduction disease, G6PD, SJS/TEN history). Overrides possible — but conscious and logged.",
    ],
  },
  {
    title: "7 · Diabetes and its comorbidity constellation",
    pains: [
      "CKD, HF, ASCVD, HTN, thyroid, fatty liver — each changes drug choice, dose and monitoring.",
      "Tracking eGFR and HbA1c trends across paper files is fantasy; calculators live in apps you never open mid-consult.",
    ],
    solutions: [
      "30+ \u201CKnown Case Of\u201D chips that actively drive safety and guidance logic — tag CKD and renal dosing arms itself.",
      "AI lab-report extraction into trend tables; 10 built-in calculators auto-compute from entered labs.",
      "eGFR-aware dosing at tap-time; auto-triggered DKD and HF education kits close the loop.",
    ],
  },
  {
    title: "8 · Evidence-Based Medicine — without the reading time",
    pains: [
      "ADA standards change every January; no consultant can stay current across four specialties — yet you're judged against exactly that.",
      "EBM tools are US-centric, paywalled, and never inside the prescription moment.",
    ],
    solutions: [
      "Deterministic Clinical Guidance engine — codified guideline logic reading the patient's diagnoses, labs and comorbidities, inside the Rx screen.",
      "Guideline dose chips, cited drug monographs on tap; AI only where safe (extraction, diet), deterministic where it must be exact.",
    ],
  },
  {
    title: "9 · Diet Prescription — Indian plates, not Pinterest salads",
    pains: [
      "Dietician access is unaffordable or unavailable for most patients; \u201Csugar kam karo, salad khao\u201D is not a diet plan.",
      "Western diet PDFs (quinoa, kale, salmon) are useless against a bhakri-varan-bhaji reality — patients quietly ignore them.",
      "One diet cannot serve CKD + diabetes + heart failure at once — manual reconciliation is impossible at OPD speed.",
    ],
    solutions: [
      "Named diet protocols on tap — Dikshit, Intermittent Fasting 16:8, DASH, Low-Carb-High-Protein, Whole-Food Plant-Based — with calorie and activity-level maths done for you.",
      "Advanced Chart: condition-aware macros (CKD, HF, TB, COPD, MASLD and more) auto-detected from the visit's diagnoses and medicines; foods-to-avoid and monitoring pre-filled.",
      "7-day Indian menus with swap chips — patient dislikes a dish? One tap swaps it, calories stay matched.",
      "Basic or Advanced — printed as a branded A4 or WhatsApp-ed, in English, Marathi or Hindi.",
    ],
  },
  {
    title: "10 · Exercise Prescription — a real protocol, not \u201Cwalk daily\u201D",
    pains: [
      "\u201CRoz walking karo\u201D is the most-ignored prescription in India — no structure, no progression, no reasoning.",
      "Gyms and equipment are non-starters for most patients; generic YouTube workouts ignore knees, cardiac status and retinopathy.",
    ],
    solutions: [
      "One-tap personalised protocol in four blocks: Warm-up → Main Routine (aerobic/strength) → Yogasana Block → Cool-down — tailored to age, comorbidities and fitness level.",
      "Household-equipment progression with reasoning — chair, dupatta, water bottles → sand-filled bottles → dumbbells, week by week.",
      "Yogasana library with a clinical safety filter — asanas contraindicated by the patient's conditions are removed automatically.",
      "Dedicated muscle-strengthening track for diabesity/GLP-1 patients (lean-mass preservation), ending with a plain-language CLINICAL REASONING block and an SOS stop-rule.",
    ],
  },
  {
    title: "11 · Refills, Referrals & Certificates — the paperwork engine",
    pains: [
      "Refill patients clog the OPD for 90-second interactions; unnoticed run-outs become silent treatment gaps.",
      "Referral notes are scribbles on letterheads; certificates (pre-op fitness and the like) are re-typed from scratch every single time.",
    ],
    solutions: [
      "Refill radar predicts when each patient's medicines run out (7-day lookahead) with a one-tap WhatsApp nudge before the gap happens.",
      "Structured, branded referral notes printed with the clinical summary — plus a Network directory of your specialists with one-tap WhatsApp referral.",
      "Certificate builder (Pre-Operative Fitness and more) with vitals and labs pre-filled from the record — print-ready in a minute.",
    ],
  },
  {
    title: "12 · Analytics & Living Guidelines",
    pains: [
      "You run the practice on gut feel — no visibility into visit volumes, follow-up compliance, portal reach, or where revenue leaks.",
      "Guideline references live in your head or in stale PDFs nobody opens.",
    ],
    solutions: [
      "Analytics with actions attached, not just charts: visits, follow-up compliance, vaccine recalls, diet-share reach, portal engagement, Kit Scan Tracker and Price Coverage with quick price fill.",
      "An editable Guidelines registry (year + notes) that feeds the guidance engine — your protocols, versioned and visible to the whole team.",
    ],
  },
  {
    title: "13 · Your Brand, Everywhere",
    pains: [
      "Practice growth depends on word of mouth you can't see or steer.",
    ],
    solutions: [
      "Every Rx, diet chart, kit, portal page and WhatsApp message carries your name, logo, colours, photo and contacts — configurable in one screen with a brand-colour picker.",
      "Every patient share on WhatsApp/Instagram is unpaid, credible advertising by the most trusted referrer that exists: your own patient.",
    ],
  },
];

export const PITCH_WHATSAPP_TEXT =
  `🩺 *Prescribify — a clinical co-pilot for Indian consultants*\n\n` +
  `Built for real Indian OPD volume, brands, languages & medico-legal risk:\n\n` +
  `✅ Legible bilingual (EN/मराठी/हिंदी) prescriptions with QR verification & full letterhead\n` +
  `✅ Live ₹ cost of the Rx + one-tap "Cheapest Rx" swap (27,000+ drug price list)\n` +
  `✅ Tap-time safety: drug interactions, renal (eGFR) dosing, pregnancy, allergy & G6PD — every override audit-logged (court-ready trail)\n` +
  `✅ Auto-detected patient education kits (Insulin, DKD, Heart Failure, Inhaler, Thyroid, Blood thinners, New HTN) — printed & WhatsApp-ed in the patient's language, with YOUR branding\n` +
  `✅ Adult vaccine planner + recall reminders\n` +
  `✅ Follow-up, refill & "did the patient even open it?" tracking\n` +
  `✅ Indian diet charts (Dikshit / IF 16:8 / DASH / LCHP / WFPB) — condition-aware, 7-day menus with swaps, in 3 languages\n` +
  `✅ 4-block exercise protocols (Warm-up → Aerobic/Strength → Yogasana → Cool-down) using household equipment, with clinical reasoning\n` +
  `✅ Branded referral notes + specialist network, certificates & refill radar\n` +
  `✅ AI lab-report extraction, 10 built-in calculators, practice analytics\n` +
  `✅ Evidence-based guidance engine at the point of prescribing\n\n` +
  `Less paperwork. Fewer errors. Stronger medico-legal cover. Free marketing on every document that leaves your desk.`;

// Branded, printable pitch document — shared by the internal /pitch page and
// the login-free public /p/pitch link.
export function PitchDocument() {
  const brand = brandColor();

  return (
        <div className="bg-white rounded-xl print:rounded-none shadow print:shadow-none overflow-hidden" style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
          {/* Branded header */}
          <header className="px-8 pt-6 pb-4 border-b-2" style={{ borderColor: brand }} data-testid="pitch-header">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <LogoTile className="h-14 w-14 shrink-0" padding="p-1.5" />
                <div>
                  <div className="text-xl font-extrabold leading-tight" style={{ color: brand }}>{CLINIC.name}</div>
                  {CLINIC.specialties && <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">{CLINIC.specialties}</div>}
                  <div className="text-[10px] text-slate-500 mt-0.5">{(CLINIC.phones || []).join(" · ")}{CLINIC.website ? ` · ${CLINIC.website}` : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="text-right">
                  <div className="text-sm font-bold text-slate-900">{doctorDisplay(CLINIC.doctorName)}</div>
                  {CLINIC.qualifications && <div className="text-[8.5px] text-slate-500 max-w-[200px] leading-snug">{CLINIC.qualifications}</div>}
                </div>
                <img src={doctorPhotoUrl()} alt="" className="h-14 w-14 rounded-full object-cover border-2 shrink-0" style={{ borderColor: brand }} />
              </div>
            </div>
            <div className="mt-5">
              <div className="inline-flex items-center gap-2 text-[10px] px-2.5 py-1 rounded-full text-white font-semibold uppercase tracking-wider mb-2" style={{ backgroundColor: brand }}>
                <Megaphone size={11} /> For Consultants
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900 leading-tight">
                The Clinical Co-Pilot Built for the Indian Consultant
              </h1>
              <p className="text-[13px] text-slate-600 mt-2 italic">
                "You went to medical school to practise medicine — not to fight paperwork, memory overload, and medico-legal anxiety."
              </p>
              <p className="text-[12px] text-slate-600 mt-2 leading-relaxed">
                You see 40–80 patients a day. Each expects a legible prescription, diet advice, disease education, a follow-up reminder — and a doctor who never errs, while NMC rules, consumer courts and Google-armed patients watch every line. Here is each pain point, honestly — and what this app actually does about it.
              </p>
            </div>
          </header>

          <div className="px-8 py-5 space-y-5">
            {SECTIONS.map((s) => (
              <section key={s.title} className="print-avoid-break">
                <h2 className="text-[15px] font-bold mb-2" style={{ color: brand }}>{s.title}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700 mb-1.5">The pain</div>
                    <ul className="space-y-1.5">
                      {s.pains.map((p, i) => (
                        <li key={i} className="text-[11.5px] leading-snug text-slate-700 flex gap-1.5">
                          <XCircle size={12} className="text-rose-500 shrink-0 mt-[1.5px]" /><span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1.5">What this app does</div>
                    <ul className="space-y-1.5">
                      {s.solutions.map((p, i) => (
                        <li key={i} className="text-[11.5px] leading-snug text-slate-700 flex gap-1.5">
                          <CheckCircle2 size={12} className="text-emerald-600 shrink-0 mt-[1.5px]" /><span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            ))}

            <section className="print-avoid-break rounded-lg border-2 p-4" style={{ borderColor: brand }}>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={16} style={{ color: brand }} />
                <h2 className="text-[15px] font-bold text-slate-900">The one-paragraph summary for a busy consultant</h2>
              </div>
              <p className="text-[12px] leading-relaxed text-slate-700">
                This is not an EMR that turns you into a data-entry clerk. It is a <strong>clinical co-pilot</strong> that writes legible, verifiable, bilingual, cost-transparent prescriptions in a third of the time; intercepts interaction, renal, pregnancy and allergy errors <strong>at the tap</strong> while keeping a court-ready audit trail; hands every patient branded vernacular education matched to what you just prescribed; remembers every vaccine, follow-up and refill; tracks whether patients actually <strong>opened</strong> what you sent; and quietly markets your practice on every document that leaves your desk.
              </p>
              <p className="text-[12.5px] font-semibold mt-2" style={{ color: brand }}>
                The question is not whether you can afford to use it — it's how much the current way of working is already costing you: in time, in risk, and in patients who never came back.
              </p>
            </section>
          </div>

          <footer className="px-8 py-3.5 text-white" style={{ backgroundColor: brand, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} data-testid="pitch-footer">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[12px] font-extrabold">{CLINIC.name}</div>
                {CLINIC.address && <div className="text-[9.5px] opacity-90">{CLINIC.address}</div>}
                <div className="text-[9.5px] opacity-90">
                  {(CLINIC.phones || []).length > 0 && <span>📞 {CLINIC.phones.join(" · ")} </span>}
                  {CLINIC.website && <span>· 🌐 {CLINIC.website}</span>}
                </div>
              </div>
              <div className="text-right text-[10px] font-semibold">{doctorDisplay(CLINIC.doctorName)}</div>
            </div>
          </footer>
        </div>
  );
}

const publicPitchUrl = () => `${window.location.origin}/p/pitch`;
const pitchShareText = () => `${PITCH_WHATSAPP_TEXT}\n\n🔗 Read the full pitch: ${publicPitchUrl()}`;

// Internal page — inside the app shell, with share/copy/print tools.
export default function PitchPage() {
  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(publicPitchUrl());
      toast.success("Public link copied — colleagues can open it without any login");
    } catch {
      toast.info(publicPitchUrl());
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 print:p-0" data-testid="pitch-page">
        <div className="flex items-center justify-end gap-2 mb-3 print:hidden flex-wrap">
          <Button onClick={copyPublicLink} variant="outline" className="gap-1.5" data-testid="pitch-copy-link-btn">
            <Link2 size={15} /> Copy public link
          </Button>
          <Button onClick={() => openWhatsAppRaw("", pitchShareText())} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="pitch-whatsapp-btn">
            <MessageCircle size={15} /> Share short version
          </Button>
          <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-700 text-white gap-1.5" data-testid="pitch-print-btn">
            <Printer size={15} /> Print / PDF
          </Button>
        </div>
        <PitchDocument />
      </div>
    </Layout>
  );
}

// Login-free public view — /p/pitch — no app chrome, safe to share anywhere.
export function PublicPitch() {
  return (
    <div className="min-h-screen bg-slate-100 print:bg-white py-6 print:py-0">
      <div className="max-w-4xl mx-auto px-4 print:px-0" data-testid="public-pitch-page">
        <div className="flex items-center justify-end gap-2 mb-3 print:hidden">
          <Button onClick={() => openWhatsAppRaw("", pitchShareText())} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="public-pitch-whatsapp-btn">
            <MessageCircle size={15} /> Share on WhatsApp
          </Button>
          <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-700 text-white gap-1.5" data-testid="public-pitch-print-btn">
            <Printer size={15} /> Print / PDF
          </Button>
        </div>
        <PitchDocument />
        <div className="text-center text-[10px] text-slate-400 mt-4 print:hidden">
          Shared by {CLINIC.name} · This page is read-only and contains no patient data.
        </div>
      </div>
    </div>
  );
}
